import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "@/shared/i18n";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { useCampaign, useDispatches } from "../model/hooks";
import type { DispatchStatus } from "../types";

function DispatchStatusBadge({ status }: { status: DispatchStatus }) {
  const { t } = useTranslation("bulk-notifications");
  const key =
    status === "pending"
      ? "dispatchStatusPending"
      : status === "sent"
        ? "dispatchStatusSent"
        : "dispatchStatusFailed";
  const variant =
    status === "sent" ? "default" : status === "pending" ? "secondary" : "destructive";
  return (
    <Badge variant={variant} className="cf-badge-pill rounded-full">
      {t(key)}
    </Badge>
  );
}

export function DispatchesPage() {
  const { t } = useTranslation("bulk-notifications");
  const navigate = useNavigate();
  const { id: campaignId } = useParams<{ id: string }>();

  const { campaign, isLoading: isLoadingCampaign } = useCampaign(
    campaignId ?? null
  );
  const { dispatches, isLoading: isLoadingDispatches } = useDispatches(
    campaignId ?? null
  );

  const isLoading = isLoadingCampaign || isLoadingDispatches;

  if (!campaignId) {
    navigate("/notifications/campaigns");
    return null;
  }

  if (isLoading && !campaign) {
    return (
      <div className="creator-financial-page -m-6 space-y-8 p-8 pb-8">
        <Button
          variant="outline"
          className="cf-btn-outline text-[var(--cf-text)]"
          onClick={() => navigate("/notifications/campaigns")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
          {t("back")}
        </Button>
        <div className="cf-card">
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-[var(--cf-text-muted)] mb-4" aria-hidden />
            <p className="text-sm text-[var(--cf-text-muted)]">{t("loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="creator-financial-page -m-6 space-y-8 p-8 pb-8">
      <Button
        variant="outline"
        className="cf-btn-outline text-[var(--cf-text)]"
        onClick={() => navigate("/notifications/campaigns")}
      >
        <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
        {t("back")}
      </Button>
      <div>
        <h1 className="cf-page-title">
          {t("dispatches")} {campaign ? `— ${campaign.title}` : ""}
        </h1>
        <p className="cf-page-subtitle">
          {dispatches.length === 0
            ? t("emptyDispatches")
            : `${dispatches.length} disparo(s)`}
        </p>
      </div>
      <div className="cf-card">
        {dispatches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Send className="h-12 w-12 text-[var(--cf-text-muted)] mb-4" aria-hidden />
            <p className="text-[var(--cf-text)] font-medium">{t("emptyDispatches")}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[var(--cf-border)] hover:bg-transparent">
                <TableHead className="text-[var(--cf-text-muted)]">{t("recipient")}</TableHead>
                <TableHead className="text-[var(--cf-text-muted)]">{t("status")}</TableHead>
                <TableHead className="text-[var(--cf-text-muted)]">{t("sentAt")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dispatches.map((d) => (
                <TableRow key={d.id} className="border-[var(--cf-border)] hover:bg-[rgba(255,255,255,0.03)] transition-colors">
                  <TableCell className="font-medium text-[var(--cf-text)]">
                    {d.recipientLabel}
                  </TableCell>
                  <TableCell>
                    <DispatchStatusBadge status={d.status} />
                  </TableCell>
                  <TableCell className="text-[var(--cf-text-muted)] text-sm">
                    {d.sentAt
                      ? format(new Date(d.sentAt), "dd/MM/yyyy HH:mm", {
                          locale: ptBR,
                        })
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
